import React from 'react';

// Apple Glass Tooltip component
const TooltipWrapper: React.FC<{ text: string; children?: React.ReactNode }> = ({
  text,
  children,
}) => {
  if (!text || text === '-') {
    return <span className='text-slate-300 font-light select-none'>—</span>;
  }

  return (
    <div className='relative group/cell w-full flex items-center justify-start text-left'>
      { children ? (
        children
      ) : (
        <div className='max-w-[200px] truncate whitespace-nowrap overflow-hidden text-[13px] font-medium text-slate-800'>
          { text }
        </div>
      ) }
      { /* Apple Glass Tooltip */ }
      { text && text.length > 15 && (
        <div className='absolute z-[9999] opacity-0 group-hover/cell:opacity-100 transition-opacity duration-200 pointer-events-none left-0 bottom-full mb-1.5'>
          <div className='bg-slate-900/95 text-slate-50 text-[11px] font-medium rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap backdrop-blur-md border border-white/10'>
            { text }
          </div>
        </div>
      ) }
    </div>
  );
};

// Column definitions from API or static config
const columns = [
  { title: 'Job Title' },
  { title: 'Job Reference' },
  { title: 'Applicants' },
  { title: 'Suitable' },
  { title: 'Outstanding' },
  { title: 'Shortlisted' },
  { title: 'Unsuccessful' },
  { title: 'Start Date' },
  { title: 'End Date' },
];

// Robust date formatter handling ISO strings, timestamps, and formatted dates
const formatDate = (rawDate: number | string | undefined | null) => {
  if (!rawDate || rawDate === '-' || rawDate === 'null' || rawDate === 'undefined') {
    return '—';
  }

  if (typeof rawDate === 'string' && /^\d{2}[/-]\d{2}[/-]\d{4}$/.test(rawDate.trim())) {
    return rawDate.trim();
  }

  let date: Date;
  if (typeof rawDate === 'number' || /^\d+$/.test(String(rawDate).trim())) {
    const num = Number(rawDate);
    date = num < 10000000000 ? new Date(num * 1000) : new Date(num);
  } else {
    date = new Date(rawDate);
  }

  if (isNaN(date.getTime())) {
    return String(rawDate);
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Dynamically build getColumns from titles
export const getColumns = columns.map((col) => ({
  key: col.title,
  label: col.title,
  width: col.title === 'Job Title' ? '220px' : col.title === 'Job Reference' ? '170px' : '130px',
  align: 'left' as const,
  render: (row: any) => {
    const value = row[col.title];
    const isDateColumn = col.title === 'Start Date' || col.title === 'End Date';

    if (col.title === 'Job Title') {
      return (
        <TooltipWrapper text={value}>
          <span className='font-semibold text-[13px] text-slate-900 hover:text-emerald-600 transition-colors truncate max-w-[200px] block'>
            { value || '—' }
          </span>
        </TooltipWrapper>
      );
    }

    if (col.title === 'Job Reference') {
      if (!value || value === '-') {
        return <span className='text-slate-300 font-light select-none'>—</span>;
      }
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100/90 text-slate-600 font-mono text-[11px] font-medium border border-slate-200/50 select-all'>
          { value }
        </span>
      );
    }

    if (
      col.title === 'Applicants' ||
      col.title === 'Suitable' ||
      col.title === 'Outstanding' ||
      col.title === 'Shortlisted' ||
      col.title === 'Unsuccessful'
    ) {
      if (value === undefined || value === null || value === '-' || value === '') {
        return <span className='text-slate-300 font-light select-none'>—</span>;
      }
      return (
        <span className='font-semibold text-[13px] text-slate-800 tabular-nums'>
          { value }
        </span>
      );
    }

    const displayText = isDateColumn ? formatDate(value) : value;
    return <TooltipWrapper text={displayText} />;
  },
  disableSort: true,
}));
